import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, GripVertical, Plus, CornerDownRight, Trash2, Info } from 'lucide-react';
import EditDropdown from './EditDropdown';
import LockPill from './LockPill';
import { ACCOUNT_TYPE_OPTIONS, DETAIL_TYPE_OPTIONS } from './constants';
import type { AccountCategory, AccountEditRow } from '../../../../hooks/useAccountCategories';
import type { EditModeSettings } from '../../../../hooks/useEditModeSettings';

function formatBalance(n: number | undefined | null): string {
  if (n === undefined) return '';
  if (n === null) return 'Not connected';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

interface ColumnDefs {
  hasNumber: boolean;
  hasDescription: boolean;
  hasNovala: boolean;
  hasBank: boolean;
  template: string;
  minWidth: number;
}

function buildColumnDefs(settings: EditModeSettings): ColumnDefs {
  const parts: string[] = ['24px'];
  let min = 24 + 14;
  if (settings.columns.accountNumber) { parts.push('90px'); min += 90 + 14; }
  parts.push('1fr'); min += 240;
  parts.push('220px'); min += 220 + 14;
  parts.push('220px'); min += 220 + 14;
  if (settings.columns.novalaBalance) { parts.push('130px'); min += 130 + 14; }
  if (settings.columns.bankBalance) { parts.push('140px'); min += 140 + 14; }
  parts.push('110px'); min += 110 + 14;
  parts.push('140px'); min += 140;
  return {
    hasNumber: settings.columns.accountNumber,
    hasDescription: settings.columns.description,
    hasNovala: settings.columns.novalaBalance,
    hasBank: settings.columns.bankBalance,
    template: parts.join(' '),
    minWidth: min,
  };
}

interface AccountRowProps {
  account: AccountEditRow;
  isSub?: boolean;
  cols: ColumnDefs;
  onUpdate: (id: string, patch: Partial<AccountEditRow>) => void;
  onAddAccount: (afterId: string) => void;
  onAddSubAccount: (parentId: string) => void;
  onDelete: (id: string) => void;
}

function AccountRow({ account, isSub, cols, onUpdate, onAddAccount, onAddSubAccount, onDelete }: AccountRowProps) {
  return (
    <div
      className="grid gap-3.5 items-center py-2.5 border-b border-lineSoft last:border-b-0"
      style={{ gridTemplateColumns: cols.template, minWidth: cols.minWidth, paddingLeft: isSub ? 32 : 0 }}
    >
      <button type="button" className="w-6 h-7 flex items-center justify-center text-ink4 hover:text-ink2 cursor-grab" title="Reorder" aria-label="Reorder">
        <GripVertical className="w-3.5 h-3.5" />
      </button>

      {cols.hasNumber && (
        <input
          type="text"
          value={account.number ?? ''}
          onChange={(e) => onUpdate(account.id, { number: e.target.value })}
          placeholder="1010"
          className="w-full h-10 px-3 border border-line rounded-lg text-[13.5px] text-ink2 bg-white hover:border-ink4 focus:border-teal-dark focus:outline-none focus:ring-2 focus:ring-teal-dark/15"
          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
        />
      )}

      <div>
        <input
          type="text"
          value={account.name}
          onChange={(e) => onUpdate(account.id, { name: e.target.value })}
          className="w-full h-10 px-3.5 border border-line rounded-lg text-[14px] text-ink bg-white hover:border-ink4 focus:border-teal-dark focus:outline-none focus:ring-2 focus:ring-teal-dark/15"
        />
        {cols.hasDescription && (
          <input
            type="text"
            value={account.description ?? ''}
            onChange={(e) => onUpdate(account.id, { description: e.target.value })}
            placeholder="Add a description"
            className="w-full h-8 px-3.5 mt-1 border border-line rounded-lg text-[12.5px] text-ink3 bg-white hover:border-ink4 focus:border-teal-dark focus:outline-none focus:ring-2 focus:ring-teal-dark/15"
          />
        )}
      </div>

      <EditDropdown value={account.accountType} onChange={(v) => onUpdate(account.id, { accountType: v })} options={ACCOUNT_TYPE_OPTIONS} />
      <EditDropdown value={account.detailType} onChange={(v) => onUpdate(account.id, { detailType: v })} options={DETAIL_TYPE_OPTIONS} />

      {cols.hasNovala && (
        <div
          className="text-right text-[13.5px] font-medium text-ink"
          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontVariantNumeric: 'tabular-nums' }}
        >
          {formatBalance(account.novalaBalance)}
        </div>
      )}
      {cols.hasBank && (
        <div
          className={`text-right text-[13.5px] font-medium ${account.bankBalance === null ? 'text-ink4' : 'text-ink'}`}
          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontVariantNumeric: 'tabular-nums' }}
        >
          {formatBalance(account.bankBalance)}
        </div>
      )}

      <LockPill locked={account.locked} onChange={(l) => onUpdate(account.id, { locked: l })} />
      <div className="inline-flex gap-0.5">
        <button type="button" onClick={() => onAddAccount(account.id)} title="Add account" aria-label="Add account"
          className="w-9 h-8 rounded-md text-ink3 hover:text-ink hover:bg-panel inline-flex items-center justify-center">
          <Plus className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => onAddSubAccount(account.id)} title="Add sub-account" aria-label="Add sub-account"
          className="w-9 h-8 rounded-md text-ink3 hover:text-ink hover:bg-panel inline-flex items-center justify-center">
          <CornerDownRight className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => onDelete(account.id)} title="Delete" aria-label="Delete"
          className="w-9 h-8 rounded-md text-ink3 hover:text-[#8B1538] hover:bg-[#FCE8EE] inline-flex items-center justify-center">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

interface AccountSectionProps {
  category: AccountCategory;
  defaultOpen?: boolean;
  settings: EditModeSettings;
  onUpdate: (id: string, patch: Partial<AccountEditRow>) => void;
  onAddAccount: (afterId: string, categorySlug: AccountCategory['slug']) => void;
  onAddSubAccount: (parentId: string, categorySlug: AccountCategory['slug']) => void;
  onDelete: (id: string) => void;
}

export default function AccountSection({ category, defaultOpen = false, settings, onUpdate, onAddAccount, onAddSubAccount, onDelete }: AccountSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const topLevel = category.accounts.filter((a) => !a.parentId);
  const cols = useMemo(() => buildColumnDefs(settings), [settings]);

  return (
    <div className="border border-line rounded-xl mb-5 bg-white">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-4 flex items-center gap-2.5 text-left bg-white rounded-xl"
      >
        {open ? <ChevronDown className="w-4 h-4 text-ink" /> : <ChevronRight className="w-4 h-4 text-ink" />}
        <span className="font-bold text-[18px] text-ink">{category.label} ({category.accounts.length})</span>
      </button>
      {open && (
        <div className="px-5 pb-5 overflow-x-auto">
          <div
            className="grid gap-3.5 py-2 pb-3 border-b border-line text-[13.5px] font-semibold text-ink2"
            style={{ gridTemplateColumns: cols.template, minWidth: cols.minWidth }}
          >
            <div></div>
            {cols.hasNumber && <div>Number</div>}
            <div>Account name</div>
            <div>Account type</div>
            <div>Detail type</div>
            {cols.hasNovala && <div className="text-right">Novala balance</div>}
            {cols.hasBank && <div className="text-right">Bank balance</div>}
            <div className="inline-flex items-center gap-1">Lock <Info className="w-3.5 h-3.5 text-ink3" aria-label="Lock accounts to prevent changes" /></div>
            <div>Actions</div>
          </div>
          {topLevel.map((account) => (
            <React.Fragment key={account.id}>
              <AccountRow
                account={account}
                cols={cols}
                onUpdate={onUpdate}
                onAddAccount={(id) => onAddAccount(id, category.slug)}
                onAddSubAccount={(id) => onAddSubAccount(id, category.slug)}
                onDelete={onDelete}
              />
              {category.accounts
                .filter((a) => a.parentId === account.id)
                .map((sub) => (
                  <AccountRow
                    key={sub.id}
                    account={sub}
                    isSub
                    cols={cols}
                    onUpdate={onUpdate}
                    onAddAccount={(id) => onAddAccount(id, category.slug)}
                    onAddSubAccount={(id) => onAddSubAccount(id, category.slug)}
                    onDelete={onDelete}
                  />
                ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}